import React from 'react';
import { OreModal, OreModalBox, OreModalBoxBody, OreModalBoxFooter, OreHeading, OreStack, OreText, OreInput } from '@edenredespana/oreneta';
import { ProfileTypeSelector } from '../molecules/ProfileTypeSelector';
import { ProfileModalActions } from '../atoms/ProfileModalActions';
import { useCreateProfileModal } from '../hooks/useCreateProfileModal';
import { useTranslation } from 'react-i18next';
import { accessRolesTranslation } from '@/AccessRoles/ui/AccesRolesTranslation';
import { GroupType, MerchantType, ModalMode } from '../../domain/ProfileTypes';
import { AccessRolesModel } from '@/AccessRoles/domain';

interface OreModalCreateProfileProps {
  open: boolean;
  onClose: () => void;
  mode?: ModalMode;
  groupType: GroupType;
  profileData?: AccessRolesModel;
  
}

export const OreModalCreateProfile: React.FC<OreModalCreateProfileProps> = ({ 
  open, 
  onClose, 
  groupType,
  mode = 'create',
  profileData 
}) => {
  const { t } = useTranslation();
  const { 
    form: {register, handleSubmit, formState}, 
    errors, 
    rolName,
    onFormSubmit,
  } = useCreateProfileModal(onClose, mode, profileData);
  function getProfileType(groupType?: GroupType): MerchantType {
    if (!groupType) return 2;
    if (groupType.isIndependent) return 3;
    if (groupType.isParent || groupType.isChildCommercial) return 1;
    if (groupType.isChildWithHardDependency) return 5;
    if (groupType.isMasterGroup) return 4;
    return 2;
  }
  const profileType = getProfileType(groupType);

  return (
    <OreModal open={open} handleOnClose={onClose}>
      <OreModalBox size="large" handleOnClose={onClose}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <OreModalBoxBody>
            {profileType === 4 ? 
              (<OreHeading align="left" size="headline-md" style={{ fontSize: '28px', fontWeight: 700 }}>
                {mode === 'edit' 
                  ? t(accessRolesTranslation.modal.editTitle)
                  : t(accessRolesTranslation.modal.master.title)
                }
              </OreHeading>): (
              <OreHeading align="left" size="headline-md" style={{ fontSize: '28px', fontWeight: 700 }}>
                {mode === 'edit' 
                  ? t(accessRolesTranslation.modal.editTitle)
                  : t(accessRolesTranslation.modal.title)
                }
              </OreHeading>
            )}
            <OreStack space="large">
              {mode === 'edit' ? (
                <OreStack direction='row' space='3xlarge'>
                  <OreStack direction='column'>
                    <OreHeading size="headline-sm" style={{ fontSize: '16px', fontWeight: 700 }}>
                      {t(accessRolesTranslation.modal.nameAndSurnames)}
                    </OreHeading>
                    <OreText style={{ fontSize: '16px', fontWeight: 400}}>
                      {`${profileData?.name} ${profileData?.surname} ${profileData?.surname2 || ''}`.trim()}
                    </OreText>
                  </OreStack>
                  <OreStack direction='column'>
                    <OreHeading size="headline-sm" style={{ fontSize: '16px', fontWeight: 700 }}>
                      {t(accessRolesTranslation.modal.mail)}
                    </OreHeading>
                    <OreText style={{ fontSize: '16px', fontWeight: 400 }}>{profileData?.email}</OreText>
                  </OreStack>
                </OreStack>
              ) : (
                <>
                {profileType === 4 && mode === 'create' ? (<OreStack>
                  <OreText tone="neutral-600">
                    {t(accessRolesTranslation.modal.master.descLine1)}
                    <b>{t(accessRolesTranslation.modal.master.descLine1_1)}</b>
                  </OreText>
                   <OreText tone="neutral-600">
                    {t(accessRolesTranslation.modal.master.descLine2)}
                    <b>{t(accessRolesTranslation.modal.master.descLine2_1)}</b>
                    {t(accessRolesTranslation.modal.master.descLine2_2)}
                    <b>{t(accessRolesTranslation.modal.master.descLine2_3)}</b>
                    {t(accessRolesTranslation.modal.master.descLine2_4)}
                  </OreText>
                  <OreText tone="neutral-600">
                    {t(accessRolesTranslation.modal.master.descLine3)}
                  </OreText>
                </OreStack>): ( 
                  <OreText tone="neutral-600">
                    {t(accessRolesTranslation.modal.desc)}
                  </OreText>)}
        
                  <OreHeading size="headline-sm">
                    {t(accessRolesTranslation.modal.personalData)}
                  </OreHeading>
                  <OreStack direction='row'>
                    <OreInput
                      {...register('name')}
                      {...errors('name')}
                      label={t(accessRolesTranslation.modal.form.namelabel)}
                      required
                      placeholder={t(accessRolesTranslation.modal.form.nameplaceHolder)}
                    />
                    <OreInput
                      {...register('surname')}
                      {...errors('surname')}
                      label={t(accessRolesTranslation.modal.form.surname1label)}
                      required
                      placeholder={t(accessRolesTranslation.modal.form.surname1placeHolder)}
                    />
                    <OreInput
                      {...register('surname2')}
                      {...errors('surname2')}
                      label={t(accessRolesTranslation.modal.form.surname2label)}
                      placeholder={t(accessRolesTranslation.modal.form.surname2placeHolder)}
                    />
                  </OreStack>
                  <OreInput
                      {...register('email')}
                      {...errors('email')}
                      label={t(accessRolesTranslation.modal.form.maillabel)}
                      required
                      placeholder={t(accessRolesTranslation.modal.form.mailplaceHolder)}
                      type='email'
                  />
                </>
              )}
              
              <OreHeading size="headline-sm" style={{ fontSize: '24px', fontWeight: 700 }}>
                {t(accessRolesTranslation.modal.useProfile)}
              </OreHeading>
              <ProfileTypeSelector
                register={register}
                rolName={rolName}
                profileType={profileType}
              />
              {mode === 'create' && (
                <OreText tone="neutral-600">
                  {t(accessRolesTranslation.modal.required)}
                </OreText>
              )}
            </OreStack>
          </OreModalBoxBody>
          <OreModalBoxFooter>
            <ProfileModalActions onCancel={onClose} mode={mode} isDirty={formState.isDirty} />
          </OreModalBoxFooter>
        </form>
      </OreModalBox>
    </OreModal>
  );
};
